import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, Button, Chip,
  Tabs, Tab, Box, CircularProgress, makeStyles,
} from '@material-ui/core';
import { withdrawalService } from '../services';
import { fmtDateTimeIST } from '../components/analytics/format';

const useStyles = makeStyles(() => ({
  header: { fontWeight: 600, marginBottom: 16 },
  wrap: { padding: 18 },
  tabs: { borderBottom: '1px solid #e5e7eb', marginBottom: 12 },
  amount: { fontWeight: 700 },
  sub: { color: '#6b7280', fontSize: 12 },
  muted: { color: '#9ca3af' },
  actions: { display: 'flex', gap: 8 },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 12 },
}));

const STATUS_TABS = [
  { value: 'requested', label: 'Requested' },
  { value: 'processing', label: 'Processing' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: '', label: 'All' },
];

const STATUS_COLOR: Record<string, any> = {
  requested: { background: '#fff7ed', color: '#c2410c' },
  processing: { background: '#eff6ff', color: '#1d4ed8' },
  paid: { background: '#ecfdf5', color: '#047857' },
  failed: { background: '#fef2f2', color: '#b91c1c' },
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

type Withdrawal = {
  _id: string;
  amount: number;
  fee: number;
  netAmount: number;
  status: string;
  payoutSnapshot?: { type?: string; label?: string; upiId?: string; accountNumberMasked?: string; bankName?: string };
  failureReason?: string | null;
  requestedOn: string;
  processedOn?: string | null;
  tutor?: { _id: string; name?: string; mobile?: string; email?: string } | null;
};

const payoutText = (p?: Withdrawal['payoutSnapshot']) => {
  if (!p) return '—';
  if (p.type === 'upi') return `UPI · ${p.upiId || p.label || ''}`;
  if (p.type === 'bank') return `Bank · ${p.bankName || ''} ${p.accountNumberMasked || ''}`.trim();
  return p.label || '—';
};

const WithdrawalsScreen: React.FC = () => {
  const classes = useStyles();
  const [status, setStatus] = useState('requested');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<Withdrawal[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await withdrawalService.list(status, page, 20);
    if (res.ok) {
      setRows(res.data?.withdrawals || []);
      setTotal(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    }
    setLoading(false);
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (w: Withdrawal) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Mark ${inr(w.netAmount)} to ${w.tutor?.name || 'tutor'} as PAID? Confirm you've completed the transfer.`)) return;
    setBusyId(w._id);
    const res = await withdrawalService.approve(w._id);
    setBusyId('');
    if (!res.ok) { alert(res.error || 'Failed to approve'); return; }
    load();
  };

  const handleReject = async (w: Withdrawal) => {
    // eslint-disable-next-line no-restricted-globals
    const reason = window.prompt(`Reject ${inr(w.netAmount)} request from ${w.tutor?.name || 'tutor'}? Enter a reason:`, '');
    if (reason === null) return;
    setBusyId(w._id);
    const res = await withdrawalService.reject(w._id, reason);
    setBusyId('');
    if (!res.ok) { alert(res.error || 'Failed to reject'); return; }
    load();
  };

  return (
    <div>
      <Typography variant="h5" className={classes.header}>Withdrawals</Typography>

      <Paper className={classes.wrap} elevation={1}>
        <Tabs
          className={classes.tabs}
          value={status}
          onChange={(_, v) => { setPage(1); setStatus(v); }}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {STATUS_TABS.map((t) => <Tab key={t.value || 'all'} value={t.value} label={t.label} />)}
        </Tabs>

        {loading ? (
          <Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tutor</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payout to</TableCell>
                  <TableCell>Requested (IST)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((w) => {
                  const actionable = w.status === 'requested' || w.status === 'processing';
                  return (
                    <TableRow key={w._id}>
                      <TableCell>
                        <Typography variant="body2">{w.tutor?.name || <span className={classes.muted}>Unknown</span>}</Typography>
                        <div className={classes.sub}>{w.tutor?.mobile || ''}</div>
                      </TableCell>
                      <TableCell>
                        <span className={classes.amount}>{inr(w.netAmount)}</span>
                        {w.fee ? <div className={classes.sub}>{inr(w.amount)} − {inr(w.fee)} fee</div> : null}
                      </TableCell>
                      <TableCell><Typography variant="body2">{payoutText(w.payoutSnapshot)}</Typography></TableCell>
                      <TableCell>{fmtDateTimeIST(w.requestedOn)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={w.status} style={STATUS_COLOR[w.status] || {}} />
                        {w.status === 'failed' && w.failureReason ? <div className={classes.sub}>{w.failureReason}</div> : null}
                      </TableCell>
                      <TableCell align="right">
                        {actionable ? (
                          <div className={classes.actions} style={{ justifyContent: 'flex-end' }}>
                            <Button size="small" variant="contained" color="primary" disabled={busyId === w._id} onClick={() => handleApprove(w)}>Approve</Button>
                            <Button size="small" variant="outlined" disabled={busyId === w._id} onClick={() => handleReject(w)}>Reject</Button>
                          </div>
                        ) : (
                          <span className={classes.muted}>—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography variant="body2" color="textSecondary">No withdrawals.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            </div>

            <div className={classes.pager}>
              <Typography variant="body2" color="textSecondary">{total} total</Typography>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Typography variant="body2">Page {page} / {totalPages}</Typography>
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </>
        )}
      </Paper>
    </div>
  );
};

export default WithdrawalsScreen;
